import { all, delay, put, race, takeEvery, takeLeading, take } from 'redux-saga/effects';

import * as httpUtils from '~/common/utils/http';
import takeRequest from '~/store/takeRequest';
import * as services from '~/services';
import { SET_HIDDEN_CONTRACT_FIELDS } from '~/store/hiddenContractFields/action';

import * as TicketActions from '~/store/tickets/actions';
import * as UtilitiesActions from '~/store/utilities/actions';
import * as UserActions from './actions';

export function* getSessionTokenStart(): Generator {
  const token = localStorage.getItem('authenticationToken');
  const hiddenContractFieldsStr = localStorage.getItem('hiddenContractFields');

  if (hiddenContractFieldsStr && hiddenContractFieldsStr !== 'null' && hiddenContractFieldsStr !== 'undefined') {
    const hiddenContractFields = JSON.parse(hiddenContractFieldsStr);
    yield put({
      type: SET_HIDDEN_CONTRACT_FIELDS,
      loading: false,
      payload: hiddenContractFields,
    });
  }

  if (!token || token === 'null') {
    yield put(UserActions.getSessionToken.failure(false));
  } else {
    httpUtils.reloadAllTokens(services, token);
    yield put(UserActions.getLoggedUser.start());
    yield put(UserActions.getSessionToken.success({ token }));
  }
}

export function* loginSuccess({ payload: response }): Generator {
  const authenticationToken = response.authenticationToken || response.token;
  localStorage.setItem('authenticationToken', authenticationToken);
  const hiddenFields = response.hiddenContractFilters;
  localStorage.setItem('hiddenContractFields', JSON.stringify(hiddenFields));
  localStorage.setItem('authMethod', 'basic');
  yield put(UserActions.getSessionToken.start());
  yield put({
    type: SET_HIDDEN_CONTRACT_FIELDS,
    loading: false,
    payload: hiddenFields,
  });
}

export function* oAuthLoginSuccess({ payload: response }): Generator {
  const authenticationToken = response.authenticationToken || response.token;
  localStorage.setItem('authenticationToken', authenticationToken);
  localStorage.setItem('authMethod', 'google');
  yield put(UserActions.getSessionToken.start());
}

export const LOGOUT_SIDE_EFFECTS_TIMEOUT = 5_000;

export type LogoutPayload = {
  userId?: number;
  userName?: string;
  redirectTo?: string;
  logoutTime?: Date;
};

export type LogoutAction = { type: string; payload?: LogoutPayload };

export function* logoutStart({ payload }: LogoutAction) {
  if (payload?.userId) {
    yield put(
      TicketActions.updateTicketDistributionFlag.start({
        id: payload?.userId,
        loggedUser: payload?.userName?.trim() || `Agente ${payload?.userId}`,
        tela: 'Logout',
        enabled: false,
      }),
    );

    yield race({
      done: all([
        take([
          TicketActions.updateTicketDistributionFlag.success.toString(),
          TicketActions.updateTicketDistributionFlag.failure.toString(),
        ]),
        take([UtilitiesActions.sendEvent.success.toString(), UtilitiesActions.sendEvent.failure.toString()]),
      ]),
      timeout: delay(LOGOUT_SIDE_EFFECTS_TIMEOUT),
    });
  }

  if (payload?.redirectTo) {
    localStorage.setItem('redirectTo', payload.redirectTo);
    localStorage.setItem('logoutTime', new Date(payload.logoutTime || Date.now()).toISOString());
  }

  localStorage.removeItem('authenticationToken');
  localStorage.removeItem('authMethod');

  httpUtils.reloadAllTokens(services, null);

  yield put(UserActions.logout.success());
}

function logoutSuccess() {
  window.location.href = '/login';
}

export function* authenticationSaga(): Generator {
  yield takeRequest(UserActions.login, { retry: 1 });
  yield takeRequest(UserActions.getLoggedUser);
  yield takeRequest(UserActions.fetchUsers);
  yield takeRequest(UserActions.createUser);
  yield takeRequest(UserActions.googleLogin);
  yield takeRequest(UserActions.refreshLogin);

  yield takeEvery(UserActions.login.success.toString(), loginSuccess);
  yield takeEvery(UserActions.getSessionToken.start.toString(), getSessionTokenStart);
  yield takeLeading(UserActions.logout.start.toString(), logoutStart);
  yield takeEvery(UserActions.googleLogin.success.toString(), oAuthLoginSuccess);
  yield takeEvery(UserActions.logout.success.toString(), logoutSuccess);
}
